import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, LibraryBig, Search, X } from 'lucide-react';

type RequestLibrary = {
    clientId?: string | null;
    libraryId?: string | null;
    client?: {
        name?: string | null;
    } | null;
    library?: {
        id?: string | null;
        name?: string | null;
    } | null;
} | null;

type RequestLibraryCardsProps = {
    requests?: RequestLibrary[] | null;
    selectedClientIds: string[];
    selectedLibraryIds: string[];
    onClientsSelect: (clientIds: string[]) => void;
    onLibrariesSelect: (libraryIds: string[]) => void;
};

type FilterOption = {
    id: string;
    name: string;
    description?: string;
};

type MultiSelectDropdownProps = {
    label: string;
    options: FilterOption[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    searchPlaceholder: string;
};

const MultiSelectDropdown = ({
    label,
    options,
    selectedIds,
    onChange,
    searchPlaceholder,
}: MultiSelectDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
    const searchLower = search.trim().toLowerCase();
    const filteredOptions = options.filter(option =>
        !searchLower
        || option.name.toLowerCase().includes(searchLower)
        || option.description?.toLowerCase().includes(searchLower));

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, []);

    const toggle = (id: string) => {
        onChange(selected.has(id)
            ? selectedIds.filter(selectedId => selectedId !== id)
            : [...selectedIds, id]);
    };

    return (
        <div className="relative w-full sm:w-64" ref={dropdownRef}>
            <button
                type="button"
                className="btn btn-secondary w-full justify-between"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(open => !open)}
            >
                <span>{label}{selectedIds.length > 0 && ` (${selectedIds.length})`}</span>
                <ChevronDown size={15} />
            </button>

            {isOpen && (
                <div
                    className="absolute right-0 z-50 mt-1 w-full rounded-lg border overflow-hidden"
                    style={{
                        backgroundColor: 'var(--bg-card)',
                        borderColor: 'var(--border-default)',
                        boxShadow: 'var(--shadow-lg)',
                    }}
                >
                    <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: 'var(--border-default)' }}>
                        <Search size={14} style={{ color: 'var(--text-tertiary)' }} />
                        <input
                            type="text"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            placeholder={searchPlaceholder}
                            className="flex-1 min-w-0 text-sm outline-none"
                            style={{ background: 'transparent', color: 'var(--text-primary)', border: 'none' }}
                            autoFocus
                        />
                    </div>
                    <div className="max-h-72 overflow-auto py-1" role="listbox" aria-multiselectable="true">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-4 text-sm text-center" style={{ color: 'var(--text-tertiary)' }}>
                                No options found
                            </div>
                        ) : filteredOptions.map(option => (
                            <label
                                key={option.id}
                                className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer"
                                style={{ color: 'var(--text-primary)' }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.has(option.id)}
                                    onChange={() => toggle(option.id)}
                                />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate">{option.name}</span>
                                    {option.description && (
                                        <span className="block text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                                            {option.description}
                                        </span>
                                    )}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const RequestLibraryCards = ({
    requests,
    selectedClientIds,
    selectedLibraryIds,
    onClientsSelect,
    onLibrariesSelect,
}: RequestLibraryCardsProps) => {
    const libraries = useMemo(() => {
        const counts = new Map<string, {
            id: string;
            name: string;
            clientId: string;
            clientName: string;
            count: number;
        }>();
        for (const request of requests ?? []) {
            const id = request?.library?.id ?? request?.libraryId;
            const name = request?.library?.name;
            const clientId = request?.clientId;
            if (!id || !name || !clientId) continue;
            const existing = counts.get(id);
            if (existing) existing.count++;
            else counts.set(id, {
                id,
                name,
                clientId,
                clientName: request?.client?.name ?? 'Unknown client',
                count: 1,
            });
        }
        return Array.from(counts.values()).sort((left, right) =>
            right.count - left.count
            || left.clientName.localeCompare(right.clientName)
            || left.name.localeCompare(right.name));
    }, [requests]);

    const clients = useMemo(() => {
        const names = new Map<string, string>();
        for (const library of libraries) names.set(library.clientId, library.clientName);
        return Array.from(names, ([id, name]) => ({ id, name }))
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [libraries]);

    const visibleLibraries = useMemo(() => {
        if (selectedClientIds.length === 0) return libraries;
        const selectedClients = new Set(selectedClientIds);
        return libraries.filter(library => selectedClients.has(library.clientId));
    }, [libraries, selectedClientIds]);

    if (libraries.length === 0) return null;
    const featuredLibraries = libraries.slice(0, 5);
    const selectedLibraries = new Set(selectedLibraryIds);
    const total = requests?.length ?? 0;

    const toggleFeaturedLibrary = (library: typeof featuredLibraries[number]) => {
        if (selectedLibraries.has(library.id)) {
            onLibrariesSelect(selectedLibraryIds.filter(id => id !== library.id));
            return;
        }
        if (selectedClientIds.length > 0 && !selectedClientIds.includes(library.clientId)) {
            onClientsSelect([...selectedClientIds, library.clientId]);
        }
        onLibrariesSelect([...selectedLibraryIds, library.id]);
    };

    const handleClientsChange = (clientIds: string[]) => {
        onClientsSelect(clientIds);
        if (clientIds.length > 0) {
            const allowedClients = new Set(clientIds);
            const allowedLibraryIds = new Set(
                libraries.filter(library => allowedClients.has(library.clientId)).map(library => library.id));
            onLibrariesSelect(selectedLibraryIds.filter(id => allowedLibraryIds.has(id)));
        }
    };

    const hasFilters = selectedClientIds.length > 0 || selectedLibraryIds.length > 0;

    return (
        <div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {featuredLibraries.map((library) => {
                    const isActive = selectedLibraries.has(library.id);
                    const percentage = total > 0 ? Math.round((library.count / total) * 100) : 0;
                    return (
                        <button
                            type="button"
                            key={library.id}
                            className="card px-4 py-3 cursor-pointer transition-all text-left"
                            style={isActive ? {
                                backgroundColor: 'var(--surface-50)',
                                borderColor: 'var(--surface-600)',
                                boxShadow: 'inset 3px 0 0 var(--brand-green)',
                            } : {}}
                            onClick={() => toggleFeaturedLibrary(library)}
                            title={isActive
                                ? `Remove ${library.clientName} / ${library.name} filter`
                                : `Include requests for ${library.clientName} / ${library.name}`}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: 'var(--surface-100)' }}
                                >
                                    <LibraryBig size={16} style={{ color: 'var(--text-secondary)' }} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{library.count}</div>
                                    <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{library.clientName}</div>
                                    <div className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>{library.name}</div>
                                </div>
                                <span
                                    className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: 'var(--surface-100)', color: 'var(--text-secondary)' }}
                                >
                                    {percentage}%
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-end gap-2 mt-3">
                {hasFilters && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                            onClientsSelect([]);
                            onLibrariesSelect([]);
                        }}
                    >
                        <X size={14} />
                        Clear filters
                    </button>
                )}
                <MultiSelectDropdown
                    label="Clients"
                    options={clients}
                    selectedIds={selectedClientIds}
                    onChange={handleClientsChange}
                    searchPlaceholder="Search clients"
                />
                <MultiSelectDropdown
                    label="Libraries"
                    options={visibleLibraries.map(library => ({
                        id: library.id,
                        name: library.name,
                        description: selectedClientIds.length === 0 ? library.clientName : `${library.count} requests`,
                    }))}
                    selectedIds={selectedLibraryIds}
                    onChange={onLibrariesSelect}
                    searchPlaceholder="Search libraries"
                />
            </div>
        </div>
    );
};

export default RequestLibraryCards;
